import axios from "axios";
import axiosInstance from "@/utils/axiosInstance";

export interface ApplicantAuthErrorPayload {
  error_code: string;
  detail: string;
  retry_after_seconds?: number | null;
  retry_allowed?: boolean;
}

export class ApplicantAuthError extends Error {
  payload: ApplicantAuthErrorPayload;
  status?: number;

  constructor(payload: ApplicantAuthErrorPayload, status?: number) {
    super(payload.detail);
    this.name = "ApplicantAuthError";
    this.payload = payload;
    this.status = status;
  }
}

export function isApplicantAuthError(error: unknown): error is ApplicantAuthError {
  return error instanceof ApplicantAuthError;
}

function authErrorPayloadFromResponse(data: unknown): ApplicantAuthErrorPayload | null {
  if (!data || typeof data !== "object") {
    return null;
  }

  const maybePayload = data as Record<string, unknown>;
  const nested = maybePayload.detail;
  const source =
    nested && typeof nested === "object" && "error_code" in nested
      ? (nested as Record<string, unknown>)
      : maybePayload;

  if (typeof source.error_code !== "string" || typeof source.detail !== "string") {
    if (typeof maybePayload.detail === "string") {
      return {
        error_code: "AUTH_REQUEST_FAILED",
        detail: maybePayload.detail,
        retry_after_seconds: null,
        retry_allowed: true,
      };
    }
    return null;
  }

  const retryAfter =
    typeof source.retry_after_seconds === "number" ? source.retry_after_seconds : null;
  return {
    error_code: source.error_code,
    detail: source.detail,
    retry_after_seconds: retryAfter,
    retry_allowed:
      typeof source.retry_allowed === "boolean" ? source.retry_allowed : undefined,
  };
}

function throwApplicantAuthError(error: unknown, fallback: string): never {
  if (axios.isAxiosError(error)) {
    const payload = authErrorPayloadFromResponse(error.response?.data);
    if (payload) {
      throw new ApplicantAuthError(payload, error.response?.status);
    }

    if (!error.response) {
      throw new ApplicantAuthError(
        {
          error_code: "NETWORK_ERROR",
          detail: "Could not reach the authentication service. Please check your connection and retry.",
          retry_after_seconds: 30,
          retry_allowed: true,
        },
        undefined
      );
    }

    throw new ApplicantAuthError(
      {
        error_code: "AUTH_REQUEST_FAILED",
        detail: fallback,
        retry_after_seconds: null,
        retry_allowed: true,
      },
      error.response.status
    );
  }

  throw error;
}

export interface StudentProfile {
  id: number;
  name: string;
  phone: string;
  email?: string | null;
  tenant_id: number;
  created_at?: string | null;
}

export interface ApplicantSubmission {
  id: number;
  tenant_id: number;
  form_id: number;
  student_id: number;
  program_code?: string | null;
  academic_year: number;
  workflow_config_id: number;
  form_version_id?: number | null;
  data: Record<string, unknown>;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ApplicantDocumentRequirement {
  document_type: string;
  label: string;
  required: boolean;
  uploaded: boolean;
}

export interface ApplicantEvidenceMethod {
  method_key: string;
  display_label: string;
  display_description: string;
  supported: boolean;
  status: string;
  applicant_action: string;
}

export interface ApplicantEvidenceDocumentType {
  document_type: string;
  display_label: string;
  uploaded: boolean;
}

export interface ApplicantEvidenceArtifact {
  form_version_id: number;
  evidence_contract_revision_id?: number | null;
  contract_hash?: string | null;
  requirement_key: string;
  method_key: string;
  document_type?: string | null;
  document_id?: string | null;
  collection_status: string;
  collected_at: string;
}

export interface ApplicantEvidenceRequirement {
  requirement_key: string;
  display_label: string;
  display_description?: string;
  accepted_methods: ApplicantEvidenceMethod[];
  accepted_document_types: ApplicantEvidenceDocumentType[];
  required: boolean;
  completion_state: string;
  source: string;
  required_for_fields: string[];
  artifacts: ApplicantEvidenceArtifact[];
}

export interface ApplicantDocumentItem {
  document_id: string;
  document_type: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  uploaded_at: string;
  status: string;
  scan_status: string;
  scan_mode?: string | null;
  scanned_at?: string | null;
  storage_provider?: string | null;
}

export interface ApplicantDocumentList {
  submission_id: number;
  complete: boolean;
  requirements: ApplicantDocumentRequirement[];
  documents: ApplicantDocumentItem[];
  evidence_requirements: ApplicantEvidenceRequirement[];
}

export interface ApplicantDraftDocumentItem {
  id: number;
  document_id: string;
  document_type: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  uploaded_at: string;
  status: string;
  scan_status: string;
  scan_mode?: string | null;
  storage_provider?: string | null;
}

export interface ApplicantDraftEvidenceList {
  draft_id: number;
  complete: boolean;
  evidence_requirements: ApplicantEvidenceRequirement[];
  documents: ApplicantDraftDocumentItem[];
}

export interface ApplicantDraftEvidenceUploadResult {
  draft_id: number;
  complete: boolean;
  document: ApplicantDraftDocumentItem;
  evidence_requirements: ApplicantEvidenceRequirement[];
}

export interface ApplicantDraftEvidenceDeleteResult {
  draft_id: number;
  document_id: string;
  deleted: boolean;
  complete: boolean;
  evidence_requirements: ApplicantEvidenceRequirement[];
}

export interface DocumentEvidenceReadiness {
  ready: boolean;
  environment: string;
  external_student_intake_enabled: boolean;
  upload_enabled: boolean;
  max_upload_configured: boolean;
  storage_provider: string;
  storage_ready: boolean;
  storage_stop_reasons: string[];
  scan_mode?: string | null;
  clamav_host_configured: boolean;
  environment_proof_required: boolean;
  environment_proof_acknowledged: boolean;
  access_policy: string;
  evidence_authority: string;
  transitional_persistence: string;
  normalized_schema_status: string;
  recoverable_json_backfill_status: string;
  json_fallback_status: string;
  legacy_unavailable_handling: string;
  stop_reasons: string[];
}

export interface ApplicantReadiness {
  environment: string;
  external_student_intake_enabled: boolean;
  can_accept_real_submissions: boolean;
  otp_rate_limit_fail_closed: boolean;
  otp_delivery_configured: boolean;
  document_upload_ready: boolean;
  document_evidence_readiness: DocumentEvidenceReadiness;
  resubmission_ready: boolean;
  mobile_low_bandwidth_guidance: string[];
  stop_reasons: string[];
}

export interface ApplicantForm {
  id: number;
  form_id: number;
  form_version_id: number;
  active_form_version_id: number;
  name: string;
  description?: string | null;
  version: string;
  program_code?: string | null;
  academic_year: number;
  runtime_source: "active_form_versions";
  evidence_requirements: ApplicantEvidenceRequirement[];
  schema: {
    properties?: Record<
      string,
      {
        title?: string;
        type?: string;
        minLength?: number;
        maxLength?: number;
        minimum?: number;
        maximum?: number;
        pattern?: string;
        "x-canonical-field"?: string;
        "x-field-type"?: string;
      }
    >;
    required?: string[];
  };
}

export interface ApplicantApplicationSubmissionSummary {
  id: number;
  status: string;
  form_id: number;
  form_version_id?: number | null;
  program_code?: string | null;
  academic_year: number;
  submitted_data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ApplicantApplicationDraftSummary {
  id: number;
  status: string;
  form_version_id: number;
  form_id: number;
  academic_year: number;
  data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ApplicantApplicationState {
  form: ApplicantForm;
  submission?: ApplicantApplicationSubmissionSummary | null;
  draft?: ApplicantApplicationDraftSummary | null;
  lifecycle_state: string;
  lifecycle_label: string;
  applicant_action: string;
  editable: boolean;
  submit_button_label: string;
  existing_application: boolean;
  creates_new_application: boolean;
  updates_existing_application: boolean;
  draft_supported: boolean;
  reimbursement_supported: boolean;
  repeat_submit_behavior: string;
  cognition_message: string;
  submitted_data_visible: boolean;
  draft_data_visible: boolean;
}

export interface ApplicantSubmissionCreate {
  form_version_id: number;
  academic_year: number;
  data: Record<string, unknown>;
}

export interface ApplicantApplicationDraft {
  id: number;
  tenant_id: number;
  student_id: number;
  active_form_version_id: number;
  form_version_id: number;
  form_id: number;
  academic_year: number;
  status: string;
  data: Record<string, unknown>;
  submitted_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApplicantApplicationDraftSave {
  form_version_id: number;
  academic_year: number;
  data: Record<string, unknown>;
}

export interface ApplicantApplicationDraftUpdate {
  data: Record<string, unknown>;
}

export interface ApplicantResubmissionCreate {
  data: Record<string, unknown>;
  reason?: string;
}

export async function requestApplicantOtp(tenantSlug: string, phone: string) {
  try {
    const res = await axiosInstance.post(`/auth/public/${tenantSlug}/request-otp`, {
      phone,
    });
    return res.data as { success: boolean; message: string };
  } catch (error) {
    throwApplicantAuthError(error, "Could not send OTP. Please retry.");
  }
}

export async function verifyApplicantOtp(
  tenantSlug: string,
  phone: string,
  otp: string
) {
  try {
    const res = await axiosInstance.post(`/auth/public/${tenantSlug}/verify-otp`, {
      phone,
      otp,
    });
    return res.data as {
      access_token: string;
      refresh_token: string;
      role: string;
    };
  } catch (error) {
    throwApplicantAuthError(error, "OTP verification failed. Please retry.");
  }
}

export async function getApplicantProfile() {
  const res = await axiosInstance.get<StudentProfile>("/students/me");
  return res.data;
}

export async function listApplicantForms() {
  const res = await axiosInstance.get<ApplicantForm[]>("/students/me/forms");
  return res.data;
}

export async function listApplicantApplicationStates() {
  const res = await axiosInstance.get<ApplicantApplicationState[]>(
    "/students/me/application-states"
  );
  return res.data;
}

export async function listApplicantApplicationDrafts() {
  const res = await axiosInstance.get<ApplicantApplicationDraft[]>(
    "/students/me/application-drafts"
  );
  return res.data;
}

export async function getApplicantStatusExplanations() {
  const res = await axiosInstance.get<Record<string, string>>(
    "/students/me/status-explanations"
  );
  return res.data;
}

export async function getApplicantReadiness() {
  const res = await axiosInstance.get<ApplicantReadiness>("/students/me/readiness");
  return res.data;
}

export async function listApplicantSubmissions() {
  const res = await axiosInstance.get<ApplicantSubmission[]>("/students/me/submissions");
  return res.data;
}

export async function createApplicantSubmission(payload: ApplicantSubmissionCreate) {
  const res = await axiosInstance.post<ApplicantSubmission>(
    "/students/me/submissions",
    payload
  );
  return res.data;
}

export async function saveApplicantApplicationDraft(payload: ApplicantApplicationDraftSave) {
  const res = await axiosInstance.post<ApplicantApplicationDraft>(
    "/students/me/application-drafts",
    payload
  );
  return res.data;
}

export async function updateApplicantApplicationDraft(
  draftId: number,
  payload: ApplicantApplicationDraftUpdate
) {
  const res = await axiosInstance.put<ApplicantApplicationDraft>(
    `/students/me/application-drafts/${draftId}`,
    payload
  );
  return res.data;
}

export async function submitApplicantApplicationDraft(draftId: number) {
  const res = await axiosInstance.post<ApplicantSubmission>(
    `/students/me/application-drafts/${draftId}/submit`
  );
  return res.data;
}

export async function listApplicantDraftEvidence(draftId: number) {
  const res = await axiosInstance.get<ApplicantDraftEvidenceList>(
    `/students/me/application-drafts/${draftId}/evidence`
  );
  return res.data;
}

export async function uploadApplicantDraftEvidenceDocument(
  draftId: number,
  requirementKey: string,
  methodKey: string,
  documentType: string,
  file: File
) {
  const data = new FormData();
  data.append("requirement_key", requirementKey);
  data.append("method_key", methodKey);
  data.append("document_type", documentType);
  data.append("file", file);
  const res = await axiosInstance.post<ApplicantDraftEvidenceUploadResult>(
    `/students/me/application-drafts/${draftId}/evidence/documents`,
    data,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return res.data;
}

export async function deleteApplicantDraftEvidenceDocument(
  draftId: number,
  documentId: string
) {
  const res = await axiosInstance.delete<ApplicantDraftEvidenceDeleteResult>(
    `/students/me/application-drafts/${draftId}/evidence/documents/${documentId}`
  );
  return res.data;
}

export async function resubmitApplicantSubmission(
  submissionId: number,
  payload: ApplicantResubmissionCreate
) {
  const res = await axiosInstance.post<ApplicantSubmission>(
    `/students/me/submissions/${submissionId}/resubmit`,
    payload
  );
  return res.data;
}

export async function listApplicantDocuments(submissionId: number) {
  const res = await axiosInstance.get<ApplicantDocumentList>(
    `/students/me/submissions/${submissionId}/documents`
  );
  return res.data;
}

export async function uploadApplicantDocument(
  submissionId: number,
  documentType: string,
  file: File
) {
  const data = new FormData();
  data.append("document_type", documentType);
  data.append("file", file);
  const res = await axiosInstance.post(
    `/students/me/submissions/${submissionId}/documents`,
    data,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return res.data as { complete: boolean };
}
